'use client';

import { useEffect } from 'react';

import { getENV } from '../../utils/env';
import { useConsentGatedHtml } from '../hooks';

type LocalAdProps = {
    onDone: () => void;
};

const LOCAL_AD_DONE_EVENT = 'velocity:local-ad:done';

type FlippBootstrapParams = {
    accessToken: string;
    merchantId: string;
    merchantName: string;
};

const getFlippBootstrapScript = ({ accessToken, merchantId, merchantName }: FlippBootstrapParams) => {
    const scriptSrc = `https://aq.flippenterprise.net/${merchantId}/iframe.js`;

    return `
(function() {
  var scriptId = 'flipp-storefront-script';
  var doneEventName = '${LOCAL_AD_DONE_EVENT}';

  function dispatchDone() {
    window.dispatchEvent(new CustomEvent(doneEventName));
  }

  function renderStorefront() {
    try {
      var flippContainer = document.querySelector('#flipp-container');
      if (!flippContainer || !window.Flipp || !window.Flipp.Storefront) {
        dispatchDone();
        return;
      }

      var storefront = new Flipp.Storefront({
        accessToken: ${JSON.stringify(accessToken)},
        merchantId: ${JSON.stringify(merchantId)},
        merchantNameIdentifier: ${JSON.stringify(merchantName)},
        locale: 'en-CA'
      });

      storefront.renderStorefront(flippContainer);
      dispatchDone();
    } catch (error) {
      dispatchDone();
    }
  }

  var existingScript = document.getElementById(scriptId);

  if (existingScript) {
    if (window.Flipp && window.Flipp.Storefront) {
      renderStorefront();
      return;
    }

    existingScript.addEventListener('load', renderStorefront, { once: true });
    existingScript.addEventListener('error', dispatchDone, { once: true });
    return;
  }

  var script = document.createElement('script');
  script.id = scriptId;
  script.src = ${JSON.stringify(scriptSrc)};
  script.async = true;
  script.onload = renderStorefront;
  script.onerror = dispatchDone;
  document.body.appendChild(script);
})();
`;
};

export const LocalAdV2 = ({ onDone }: LocalAdProps) => {
    const accessToken = getENV('NEXT_PUBLIC_FLIPP_ACCESS_TOKEN');
    const merchantId = getENV('NEXT_PUBLIC_FLIPP_MERCHANT_ID');
    const merchantName = getENV('NEXT_PUBLIC_FLIPP_MERCHANT_NAME');
    const isEnabled = Boolean(accessToken && merchantId && merchantName);
    // Marker div for InsertHtml + onReady inject (InsertHtml does not execute <script> payloads).
    const html = isEnabled ? '<div id="local-ad-flipp-marker"></div>' : '';

    useEffect(() => {
        window.addEventListener(LOCAL_AD_DONE_EVENT, onDone);

        return () => {
            window.removeEventListener(LOCAL_AD_DONE_EVENT, onDone);
        };
    }, [onDone]);

    useConsentGatedHtml({
        html,
        isEnabled,
        consentGroup: 'C0003',
        dedupeId: 'local-ad-flipp-marker',
        onReady: () => {
            if (!isEnabled || document.getElementById('flipp-bootstrap-runner')) return;
            const script = document.createElement('script');
            script.id = 'flipp-bootstrap-runner';
            script.textContent = getFlippBootstrapScript({ accessToken, merchantId, merchantName });
            document.body.appendChild(script);
        },
    });

    return null;
};
